import React, { Fragment } from 'react';
import { Container, Row, Col, Card, CardBody, CardHeader, Button, Badge, Table, Spinner } from 'reactstrap';
import { Breadcrumbs } from '../../../../AbstractElements';
import { useParams, useNavigate } from 'react-router-dom';
import { useGetPurchaseOrderByIdQuery, useApprovePurchaseOrderMutation, useMarkPurchaseOrderOrderedMutation } from '../../../../store/services/inventoryApi';
import { toast } from 'react-toastify';
import { Check, Send, Edit, ArrowLeft, Printer } from 'react-feather';

const PurchaseOrderDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { data: poData, isLoading, refetch } = useGetPurchaseOrderByIdQuery(id);
    const [approvePO, { isLoading: isApproving }] = useApprovePurchaseOrderMutation();
    const [orderPO, { isLoading: isOrdering }] = useMarkPurchaseOrderOrderedMutation();

    const po = poData?.data ? poData.data : poData;

    const handleApprove = async () => {
        if (window.confirm("Are you sure you want to approve this Purchase Order?")) {
            try {
                await approvePO(id).unwrap();
                toast.success("Purchase Order Approved");
                refetch();
            } catch (err) {
                toast.error(err?.data?.message || "Failed to approve PO");
            }
        }
    };

    const handleOrder = async () => {
        if (window.confirm("Mark this PO as Ordered? This indicates the order has been sent to the supplier.")) {
            try {
                await orderPO(id).unwrap();
                toast.success("Purchase Order Marked as Ordered");
                refetch();
            } catch (err) {
                toast.error(err?.data?.message || "Failed to mark as Ordered");
            }
        }
    };

    if (isLoading) return <div className="text-center p-5"><Spinner /></div>;
    if (!po) return <div className="text-center p-5">Purchase Order not found</div>;

    const grandTotal = po.items?.reduce((sum, item) => sum + (Number(item.orderedQty || 0) * Number(item.unitCost || 0)), 0) || 0;

    return (
        <Fragment>
            <Breadcrumbs mainTitle="PO Details" parent="Inventory" title={`PO-${po.poNumber || id.slice(-6)}`} />
            <Container fluid={true}>
                <Row>
                    <Col sm="12">
                        <Card>
                            <CardHeader className="d-flex justify-content-between align-items-center">
                                <div>
                                    <h5>PO #{po.poNumber} <Badge color={
                                        po.status === 'Draft' ? 'secondary' :
                                            po.status === 'Approved' ? 'info' :
                                                po.status === 'Ordered' ? 'primary' :
                                                    po.status === 'PartiallyReceived' ? 'warning' :
                                                        po.status === 'Closed' ? 'success' :
                                                            po.status === 'Received' ? 'warning' : 'success'
                                    }>{po.status}</Badge></h5>
                                    <small className="text-muted">Created on {new Date(po.createdAt).toLocaleDateString()}</small>
                                </div>
                                <div className="d-flex gap-2 d-print-none">
                                    <Button color="light" onClick={() => navigate('/inventory/purchase-orders')}><ArrowLeft size={14} className="me-1" /> Back</Button>
                                    <Button color="secondary" onClick={() => window.print()}><Printer size={14} className="me-1" /> Print</Button>

                                    {po.status === 'Draft' && (
                                        <>
                                            <Button color="warning" onClick={() => navigate(`/inventory/purchase-orders/edit/${id}`)}>
                                                <Edit size={14} className="me-1" /> Edit
                                            </Button>
                                            <Button color="success" onClick={handleApprove} disabled={isApproving}>
                                                {isApproving ? <Spinner size="sm" /> : <><Check size={14} className="me-1" /> Approve</>}
                                            </Button>
                                        </>
                                    )}

                                    {po.status === 'Approved' && (
                                        <Button color="primary" onClick={handleOrder} disabled={isOrdering}>
                                            {isOrdering ? <Spinner size="sm" /> : <><Send size={14} className="me-1" /> Mark Ordered</>}
                                        </Button>
                                    )}

                                    {['Ordered', 'PartiallyReceived'].includes(po.status) && (
                                        <Button color="info" onClick={() => navigate(`/inventory/purchase-orders/receive/${id}`)}>
                                            Receive Stock
                                        </Button>
                                    )}
                                </div>
                            </CardHeader>
                            <CardBody>
                                <Row className="mb-4">
                                    <Col md="4">
                                        <strong>Supplier:</strong><br />
                                        {po.supplierId?.name || 'N/A'}<br />
                                        {po.supplierId?.contactPerson}<br />
                                        {po.supplierId?.contactEmail}
                                    </Col>
                                    <Col md="4">
                                        <strong>Deliver To:</strong><br />
                                        {po.storeId?.storeName || 'N/A'}<br />
                                        Code: {po.storeId?.storeNumber || 'N/A'}<br />
                                        {po.storeId?.storeAddress || 'N/A'}
                                    </Col>
                                    <Col md="4">
                                        <strong>Expected Date:</strong> {po.expectedDate ? new Date(po.expectedDate).toLocaleDateString() : 'N/A'}<br />
                                        <strong>Created By:</strong> {po.createdBy?.email || 'System'}
                                    </Col>
                                </Row>

                                <Table hover responsive bordered className="mt-3">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Product</th>
                                            <th>SKU</th>
                                            <th className="text-end">Ordered Qty</th>
                                            <th className="text-end">Received Qty</th>
                                            <th className="text-end">PO Unit Price</th>
                                            <th className="text-end">Recv Unit Price</th>
                                            <th className="text-center">Price Diff (%)</th>
                                            <th className="text-end">Total</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {po.items?.map((item, index) => {
                                            const originalUnitCost = item.unitCost || 0;
                                            const receivedUnitCost = item.receivedUnitCost;
                                            let pctBadge = null;

                                            if (receivedUnitCost !== undefined && receivedUnitCost !== null && originalUnitCost > 0) {
                                                const diff = receivedUnitCost - originalUnitCost;
                                                const pct = (diff / originalUnitCost) * 100;
                                                if (pct > 0) {
                                                    pctBadge = <Badge color="success">+{pct.toFixed(2)}%</Badge>;
                                                } else if (pct < 0) {
                                                    pctBadge = <Badge color="danger">{pct.toFixed(2)}%</Badge>;
                                                } else {
                                                    pctBadge = <Badge color="secondary">0.00%</Badge>;
                                                }
                                            } else {
                                                pctBadge = <span className="text-muted">N/A</span>;
                                            }

                                            return (
                                                <tr key={index}>
                                                    <td>{item.productName}</td>
                                                    <td>{item.sku}</td>
                                                    <td className="text-end">{item.orderedQty}</td>
                                                    <td className="text-end">{item.receivedQty || 0}</td>
                                                    <td className="text-end">${originalUnitCost.toFixed(2)}</td>
                                                    <td className="text-end">
                                                        {receivedUnitCost !== undefined && receivedUnitCost !== null 
                                                            ? `$${receivedUnitCost.toFixed(2)}` 
                                                            : 'N/A'
                                                        }
                                                    </td>
                                                    <td className="text-center">{pctBadge}</td>
                                                    <td className="text-end">${((item.orderedQty || 0) * originalUnitCost).toFixed(2)}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                    <tfoot>
                                        <tr>
                                            <td colSpan="7" className="text-end"><strong>Grand Total:</strong></td>
                                            <td className="text-end"><strong>${grandTotal.toFixed(2)}</strong></td>
                                        </tr>
                                    </tfoot>
                                </Table>

                                {po.notes && (
                                    <div className="mt-4 p-3 bg-light rounded">
                                        <h6>Notes:</h6>
                                        <p className="mb-0">{po.notes}</p>
                                    </div>
                                )}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </Fragment>
    );
};

export default PurchaseOrderDetails;
